"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { ToneBadge } from "@/components/app-shell/lists/table-box";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { ModalContent, ModalHeader } from "@/components/forms/form-modal";
import { cn } from "@/lib/utils";
import { formatMoment, type DisplayFormat } from "@/lib/format-moment";
import type { SignatureSummary } from "@/modules/documents";
import { signaturePanel, type PanelRow } from "./signature-panel-rows";
import { MARK_TONES, SIGNATURE_TONES } from "./signature-tones";

const FILE_ROUTE = /^\/api\/documents\/explorer\/file\/([0-9a-f-]{36})(?:[/?]|$)/;

/** The Files id inside a document address, or null for a PDF that is not a kept file (a route that makes it on the fly). */
export const storedFileIdOf = (href: string) => FILE_ROUTE.exec(href)?.[1] ?? null;

type Signed = Extract<SignatureSummary, { signed: true }>;
type Found = { all: Signed[]; display: DisplayFormat };
const WORST = ["invalid", "changed", "untrusted", "unchecked", "valid"] as const;
const worstStatus = (all: Signed[]) => WORST.find((w) => all.some((s) => s.status === w)) ?? "valid";

/** What the site found in the signature of the kept file behind `href`, or null while loading, when the PDF is not signed or not a kept file, or when the answer fails. */
function useSignature(href: string) {
  const id = storedFileIdOf(href);
  const [found, setFound] = useState<(SignatureSummary & Partial<Found>) | null>(null);
  useEffect(() => {
    if (!id) return;
    let live = true;
    void fetch(`/api/documents/explorer/file/${id}/signature`, { credentials: "same-origin" })
      .then((r) => (r.ok ? (r.json() as Promise<SignatureSummary & Partial<Found>>) : null))
      .then((s) => { if (live) setFound(s); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [id]);
  return found?.signed && found.all && found.display ? { all: found.all, display: found.display } : null;
}

/** The badge in the header of the shared viewer for a signed PDF (green valid on a trusted list, yellow intact but not confirmed or not checked, red changed or not valid); click opens a large dialog: a verdict banner, then cards for signer, integrity, signing time, trust and long-term data (each trust line confirmed, not confirmed or could not be checked, with the reason). Shows nothing for an unsigned PDF or one not kept in Files. `openNow` opens the details at once ("Verify signature"). */
export function SignatureBadge({ href, openNow = false }: { href: string; openNow?: boolean }) {
  const t = useTranslations("pdfPreview.signature");
  const found = useSignature(href);
  if (!found) return null;
  const { all, display } = found;
  const status = worstStatus(all);
  const tone = SIGNATURE_TONES[status];
  const Verdict = tone.icon;
  const when = (iso: string | null) => (iso ? formatMoment(new Date(iso), display, "dateTime") : "-");
  const sections = all.map((s) => signaturePanel(s, (key) => t(key as never), when));
  return (
    <Dialog defaultOpen={openNow}>
      <DialogTrigger render={<button type="button" className="rounded-md" title={t("details")} />}><ToneBadge tone={tone} label={t(`status.${status}`)} /></DialogTrigger>
      <ModalContent size="lg">
        <ModalHeader icon={<Verdict />} title={t("details")} description={t("help")} />
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-6">
          <div role="status" className={cn("flex items-start gap-3 rounded-lg border p-4", tone.chip)}>
            <Verdict aria-hidden className="mt-0.5 size-6 shrink-0" />
            <div className="flex min-w-0 flex-col gap-1"><p className="text-base font-semibold">{t(`status.${status}`)}</p><p>{t(`verdict.${status}`)}</p></div>
          </div>
          {sections.map((groups, n) => (
            <div key={n} className="grid gap-4">
              {sections.length > 1 && <p className="text-sm font-semibold">{n + 1}/{sections.length} · {all[n].web?.name || all[n].signerName || "-"}</p>}
              {groups.map((g) => (
                <section key={g.id} aria-labelledby={`sig-${n}-${g.id}`} className="rounded-lg border bg-card text-card-foreground">
                  <h3 id={`sig-${n}-${g.id}`} className="border-b bg-muted/40 px-4 py-2 text-sm font-semibold">{t(`groups.${g.id}`)}</h3>
                  <dl className="divide-y">{g.rows.map((row) => <Row key={row.label} row={row} />)}</dl>
                </section>
              ))}
            </div>
          ))}
          <p className="text-xs text-muted-foreground">{t("note")}</p>
        </div>
      </ModalContent>
    </Dialog>
  );
}

/** Label at the start and value at the end on wide screens, stacked on a phone; the chip carries an icon and a word besides its colour. */
function Row({ row }: { row: PanelRow }) {
  const tone = row.mark ? MARK_TONES[row.mark] : null;
  const Icon = tone?.icon;
  return (
    <div className="grid gap-1 px-4 py-2.5 sm:grid-cols-[14rem_minmax(0,1fr)] sm:gap-4">
      <dt className="text-muted-foreground">{row.label}</dt>
      <dd className="flex min-w-0 flex-col items-start gap-1">
        {tone && Icon ? <span className={cn("inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium", tone.chip)}><Icon aria-hidden className="size-3.5" />{row.value}</span> : <span className={cn("break-words", row.mono && "select-all font-mono text-xs")} dir={row.mono ? "ltr" : undefined}>{row.value}</span>}
        {row.note && <span className="text-xs text-muted-foreground">{row.note}</span>}
      </dd>
    </div>
  );
}
